import type {
  EthereumTransaction,
  EIP712TypedData,
} from '@txviz/core';

/**
 * Input to the Ethereum plugin for regular transactions.
 */
export interface EthereumPluginTransactionInput {
  readonly transaction: EthereumTransaction;
  readonly abi?: readonly unknown[] | undefined;
}

/**
 * Union of all inputs the Ethereum plugin can handle.
 */
export type EthereumPluginInput =
  | EthereumPluginTransactionInput
  | EIP712TypedData;

/**
 * Type guard: check if input is EIP-712 typed data.
 */
export function isEIP712Input(input: EthereumPluginInput): input is EIP712TypedData {
  return 'primaryType' in input && 'domain' in input && 'types' in input;
}

/**
 * Type guard: check if input is a regular transaction.
 */
export function isTransactionInput(
  input: EthereumPluginInput,
): input is EthereumPluginTransactionInput {
  return 'transaction' in input;
}

/**
 * Well-known ERC-20 function selectors and their human-readable signatures.
 */
export const ERC20_SELECTORS: Record<string, string> = {
  '0xa9059cbb': 'transfer(address,uint256)',
  '0x23b872dd': 'transferFrom(address,address,uint256)',
  '0x095ea7b3': 'approve(address,uint256)',
};

/**
 * Well-known ERC-721 function selectors.
 */
export const ERC721_SELECTORS: Record<string, string> = {
  '0x23b872dd': 'transferFrom(address,address,uint256)',
  '0x42842e0e': 'safeTransferFrom(address,address,uint256)',
  '0xb88d4fde': 'safeTransferFrom(address,address,uint256,bytes)',
  '0x095ea7b3': 'approve(address,uint256)',
  '0xa22cb465': 'setApprovalForAll(address,bool)',
};

/**
 * Well-known ERC-1155 function selectors.
 */
export const ERC1155_SELECTORS: Record<string, string> = {
  '0xf242432a': 'safeTransferFrom(address,address,uint256,uint256,bytes)',
  '0x2eb2c2d6': 'safeBatchTransferFrom(address,address,uint256[],uint256[],bytes)',
  '0xa22cb465': 'setApprovalForAll(address,bool)',
};

/**
 * Combined map of well-known selectors for quick lookup.
 */
export const KNOWN_SELECTORS: Record<string, { name: string; standard: string }> = {
  '0xa9059cbb': { name: 'transfer', standard: 'ERC-20' },
  '0x23b872dd': { name: 'transferFrom', standard: 'ERC-20/721' },
  '0x095ea7b3': { name: 'approve', standard: 'ERC-20/721' },
  '0x42842e0e': { name: 'safeTransferFrom', standard: 'ERC-721' },
  '0xb88d4fde': { name: 'safeTransferFrom', standard: 'ERC-721' },
  '0xa22cb465': { name: 'setApprovalForAll', standard: 'ERC-721/1155' },
  '0xf242432a': { name: 'safeTransferFrom', standard: 'ERC-1155' },
  '0x2eb2c2d6': { name: 'safeBatchTransferFrom', standard: 'ERC-1155' },
};
